// src/pages/performance/AppraisalParticipantsPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../../services/api";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Divider,
  Chip,
  Alert,
  IconButton,
  Avatar,
  Tooltip,
  Breadcrumbs,
  Skeleton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import GroupIcon from "@mui/icons-material/Group";
import WorkIcon from "@mui/icons-material/Work";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

/* ---------------------------------------
   Styled (same "new design" feel)
---------------------------------------- */
const Page = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  padding: theme.spacing(1.25),
  [theme.breakpoints.up("sm")]: { padding: theme.spacing(1.5) },
  [theme.breakpoints.up("md")]: { padding: theme.spacing(2) },
  background:
    theme.hr?.gradient?.page ||
    (theme.palette.mode === "light"
      ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${theme.palette.background.default} 38%)`
      : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${theme.palette.background.default} 38%)`),
}));

const GlassCard = styled(Paper)(({ theme }) => ({
  borderRadius: "8px",
  backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.9),
  border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
  boxShadow: "none",
  overflow: "hidden",
}));

const AccentBar = styled("div")(({ theme }) => ({
  height: 4,
  width: "100%",
  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
}));

const Pill = styled(Chip)(({ theme }) => ({
  borderRadius: "8px",
  fontWeight: 900,
  borderColor: alpha(theme.palette.divider, 0.22),
}));

const StatusBadge = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "status",
})(({ theme, status }) => {
  const map = {
    draft: { bg: alpha(theme.palette.grey[500], 0.14), fg: theme.palette.text.secondary },
    pending: { bg: alpha(theme.palette.warning.main, 0.14), fg: theme.palette.warning.main },
    not_started: { bg: alpha(theme.palette.warning.main, 0.12), fg: theme.palette.warning.main },
    in_progress: { bg: alpha(theme.palette.info.main, 0.14), fg: theme.palette.info.main },
    completed: { bg: alpha(theme.palette.success.main, 0.14), fg: theme.palette.success.main },
    submitted: { bg: alpha(theme.palette.success.main, 0.14), fg: theme.palette.success.main },
    cancelled: { bg: alpha(theme.palette.error.main, 0.14), fg: theme.palette.error.main },
  };
  const c = map[String(status || "").toLowerCase()] || map.draft;
  return {
    borderRadius: "8px",
    fontWeight: 900,
    fontSize: "0.75rem",
    height: 26,
    backgroundColor: c.bg,
    color: c.fg,
    border: `1px solid ${alpha(c.fg, 0.25)}`,
    textTransform: "capitalize",
  };
});

const safeMsg = (err, fallback = "Failed to load participants") =>
  err?.response?.data?.message || err?.message || fallback;

const getSteps = (appraisal) => appraisal?.stepInstances || appraisal?.step_instances || [];

const asStr = (v) => (v == null ? "" : String(v));

const initials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase() || "U";
};

const isDone = (s) => ["completed", "submitted"].includes(String(s || "").toLowerCase());
const isActive = (s) => ["in_progress"].includes(String(s || "").toLowerCase());

/* ---------------------------------------
   Main
---------------------------------------- */
export default function AppraisalParticipantsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [appraisal, setAppraisal] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const fetchDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/performance/appraisals/${id}`);
      const payload = res?.data?.data ?? res?.data;
      setAppraisal(payload || null);
    } catch (err) {
      console.error("Failed to load appraisal participants", err);
      setError(safeMsg(err));
      setAppraisal(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const employee = useMemo(() => appraisal?.employee || null, [appraisal]);

  const steps = useMemo(() => {
    const list = getSteps(appraisal);
    return [...list].sort((a, b) => (Number(a.step_order) || 0) - (Number(b.step_order) || 0));
  }, [appraisal]);

  const workflowName = useMemo(
    () => appraisal?.workflowTemplate?.name || appraisal?.workflow_template?.name || "—",
    [appraisal]
  );

  // Build participant cards (group by user)
  const participants = useMemo(() => {
    if (!appraisal) return [];

    const map = new Map(); // key: user.id or fallback string
    const addUser = (user, kind) => {
      if (!user) return;
      const key = user?.id != null ? `u:${user.id}` : `code:${asStr(user.employee_code) || asStr(user.name)}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          kindSet: new Set([kind]),
          user: {
            id: user?.id,
            name: user?.name || "—",
            employee_code: user?.employee_code,
            department: user?.department,
            actual_role: user?.actual_role,
          },
          steps: [],
        });
      } else {
        map.get(key).kindSet.add(kind);
      }
    };

    if (employee) addUser(employee, "employee");

    steps.forEach((s) => {
      if (s?.user) addUser(s.user, "evaluator");
    });

    // attach step assignments
    steps.forEach((s) => {
      const u = s?.user;
      if (!u) return;
      const key = u?.id != null ? `u:${u.id}` : `code:${asStr(u.employee_code) || asStr(u.name)}`;
      const rec = map.get(key);
      if (!rec) return;
      rec.steps.push({
        id: s.id,
        step_order: s.step_order,
        step_name: s.step_name || s.name || `Step ${s.step_order ?? "—"}`,
        actor_type: s.actor_type,
        status: s.status,
      });
    });

    // finalize
    return Array.from(map.values()).map((x) => ({
      ...x,
      kinds: Array.from(x.kindSet),
      steps: [...x.steps].sort((a, b) => (Number(a.step_order) || 0) - (Number(b.step_order) || 0)),
    }));
  }, [appraisal, employee, steps]);

  const unassignedSteps = useMemo(() => {
    return steps
      .filter((s) => !s?.user)
      .map((s) => ({
        id: s.id,
        step_order: s.step_order,
        step_name: s.step_name || s.name || `Step ${s.step_order ?? "—"}`,
        actor_type: s.actor_type,
        status: s.status,
      }));
  }, [steps]);

  const filteredParticipants = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return participants;

    return participants.filter((p) => {
      const name = String(p.user?.name || "").toLowerCase();
      const code = String(p.user?.employee_code || "").toLowerCase();
      const dept = String(p.user?.department || "").toLowerCase();
      const role = String(p.user?.actual_role || "").toLowerCase();
      return name.includes(needle) || code.includes(needle) || dept.includes(needle) || role.includes(needle);
    });
  }, [participants, q]);

  const stats = useMemo(() => {
    const totalSteps = steps.length;
    const doneSteps = steps.filter((s) => isDone(s.status)).length;
    const activeSteps = steps.filter((s) => isActive(s.status)).length;
    const pendingSteps = totalSteps - doneSteps - activeSteps;
    return { totalSteps, doneSteps, activeSteps, pendingSteps };
  }, [steps]);

  if (loading) {
    return (
      <Page>
        <Stack spacing={1.5} sx={{ maxWidth: 1180, mx: "auto" }}>
          <Skeleton height={130} variant="rounded" sx={{ borderRadius: "8px" }} />
          <Skeleton height={360} variant="rounded" sx={{ borderRadius: "8px" }} />
        </Stack>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Box sx={{ maxWidth: 700, mx: "auto", mt: 8 }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchDetails}>
                Retry
              </Button>
            }
            sx={{ borderRadius: "8px" }}
          >
            {error}
          </Alert>

          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/performance/appraisals/${id}`)}
            sx={{ mt: 2, textTransform: "none", fontWeight: 900 }}
          >
            Back to Appraisal
          </Button>
        </Box>
      </Page>
    );
  }

  if (!appraisal) {
    return (
      <Page>
        <Box sx={{ maxWidth: 720, mx: "auto", textAlign: "center", mt: 10 }}>
          <InfoIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.6 }} />
          <Typography variant="h6" sx={{ fontWeight: 900 }} gutterBottom>
            Appraisal Not Found
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/performance/appraisals")}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}
          >
            Back to Appraisals
          </Button>
        </Box>
      </Page>
    );
  }

  return (
    <Page>
      <Box sx={{ maxWidth: 1180, mx: "auto" }}>
        {/* Header */}
        <GlassCard sx={{ mb: 1.5 }}>
          <AccentBar />
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={2}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1 }}>
                    <Link to="/performance/appraisals" style={{ textDecoration: "none" }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 800, "&:hover": { color: "primary.main" } }}
                      >
                        Appraisals
                      </Typography>
                    </Link>
                    <Link to={`/performance/appraisals/${id}`} style={{ textDecoration: "none" }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 800, "&:hover": { color: "primary.main" } }}
                      >
                        #{id}
                      </Typography>
                    </Link>
                    <Typography variant="body2" sx={{ fontWeight: 950 }}>
                      Participants
                    </Typography>
                  </Breadcrumbs>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Avatar
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        width: 38,
                        height: 38,
                      }}
                    >
                      <GroupIcon />
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.12 }}>
                      Participants
                    </Typography>
                    <Pill size="small" variant="outlined" icon={<WorkIcon fontSize="small" />} label={workflowName} />
                  </Stack>

                  <Stack direction="row" spacing={0.8} flexWrap="wrap" sx={{ mt: 0.8 }}>
                    <Pill size="small" variant="outlined" label={`Steps: ${stats.totalSteps}`} />
                    <Pill
                      size="small"
                      variant="outlined"
                      icon={<CheckCircleIcon fontSize="small" />}
                      label={`Done: ${stats.doneSteps}`}
                    />
                    <Pill size="small" variant="outlined" label={`Active: ${stats.activeSteps}`} />
                    <Pill
                      size="small"
                      variant="outlined"
                      icon={<PendingActionsIcon fontSize="small" />}
                      label={`Pending: ${stats.pendingSteps}`}
                    />
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                  <Tooltip title="Back">
                    <IconButton
                      onClick={() => navigate(`/performance/appraisals/${id}`)}
                      sx={{
                        borderRadius: "8px",
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={fetchDetails}
                      sx={{
                        borderRadius: "8px",
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              <TextField
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, code, department, role..."
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: alpha(theme.palette.background.paper, 0.75),
                  },
                }}
              />
            </Stack>
          </Box>
        </GlassCard>

        {/* Content layout (flex, no Grid) */}
        <Box sx={{ display: "flex", gap: 1.5, flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start" }}>
          {/* Participants list */}
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <GlassCard>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" sx={{ fontWeight: 950, mb: 0.5 }}>
                  People with access
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                  Employee + assigned evaluators from workflow steps (unique users).
                </Typography>

                {filteredParticipants.length === 0 ? (
                  <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: "8px" }}>
                    No participants match your search.
                  </Alert>
                ) : (
                  <Stack spacing={1.25}>
                    {filteredParticipants.map((p) => {
                      const kindsLabel = p.kinds.includes("employee") && p.kinds.includes("evaluator")
                        ? "Employee + Evaluator"
                        : p.kinds.includes("employee")
                        ? "Employee"
                        : "Evaluator";

                      const doneCount = p.steps.filter((s) => isDone(s.status)).length;
                      const activeCount = p.steps.filter((s) => isActive(s.status)).length;

                      return (
                        <Paper
                          key={p.key}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: "8px",
                            border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
                            bgcolor: alpha(theme.palette.background.default, 0.32),
                          }}
                        >
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: theme.palette.primary.main,
                                fontWeight: 950,
                              }}
                            >
                              {initials(p.user?.name)}
                            </Avatar>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                                <Typography variant="subtitle1" sx={{ fontWeight: 950 }} noWrap>
                                  {p.user?.name || "—"}
                                </Typography>
                                <Pill size="small" variant="outlined" label={kindsLabel} />
                              </Stack>

                              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {p.user?.employee_code || "—"} • {p.user?.department || "—"} • {p.user?.actual_role || "—"}
                              </Typography>

                              <Stack direction="row" spacing={0.8} flexWrap="wrap" sx={{ mt: 0.8 }}>
                                <Pill size="small" variant="outlined" label={`Assigned steps: ${p.steps.length}`} />
                                <Pill size="small" variant="outlined" icon={<CheckCircleIcon fontSize="small" />} label={`Done: ${doneCount}`} />
                                <Pill size="small" variant="outlined" label={`Active: ${activeCount}`} />
                              </Stack>
                            </Box>
                          </Stack>

                          {p.steps.length > 0 && (
                            <>
                              <Divider sx={{ my: 1.25 }} />
                              <Stack direction="row" spacing={0.8} flexWrap="wrap">
                                {p.steps.map((s) => (
                                  <StatusBadge
                                    key={`${p.key}-s-${s.id || s.step_order}`}
                                    status={s.status}
                                    label={`#${s.step_order ?? "—"} ${String(s.step_name || "").slice(0, 18)}${String(s.step_name || "").length > 18 ? "…" : ""}`}
                                  />
                                ))}
                              </Stack>
                            </>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </GlassCard>
          </Box>

          {/* Unassigned steps */}
          <Box sx={{ width: { xs: "100%", md: 340 }, flexShrink: 0 }}>
            <GlassCard>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" sx={{ fontWeight: 950, mb: 0.5 }}>
                  Unassigned steps
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                  Steps without a user assigned (needs workflow mapping or overrides).
                </Typography>

                {unassignedSteps.length === 0 ? (
                  <Alert severity="success" sx={{ borderRadius: "8px" }}>
                    All steps are assigned.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {unassignedSteps.map((s, idx) => (
                      <Paper
                        key={s.id || `${s.step_order}-${idx}`}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: "8px",
                          border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
                          bgcolor: alpha(theme.palette.warning.main, 0.06),
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>
                          Step #{s.step_order ?? "—"} • {s.step_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                          Actor: {s.actor_type || "—"}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <StatusBadge status={s.status} label={String(s.status || "draft").replaceAll("_", " ")} />
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            </GlassCard>
          </Box>
        </Box>
      </Box>
    </Page>
  );
}
